// Resilient localStorage wrapper for the SOS mode.
//
// On QuotaExceededError, we evict a *whitelist* of legacy / non-critical keys
// and retry ONCE. We NEVER evict:
//   - the auth token (`qoc_token` or `token`)
//   - the SOS queue (`pending_sos_queue`)
//   - the SOS identity card (`sos_identity_card`)
//
// This file is intentionally tiny and has no deps.

const CRITICAL_KEYS = new Set([
  "qoc_token",
  "token",
  "pending_sos_queue",
  "sos_identity_card",
]);

// Known legacy / non-critical keys that are safe to evict.
// Keep this list conservative — unknown keys stay untouched.
const SAFE_EVICTION_KEYS = [
  "ondi_chat_history",
  "ondi_tour_shown",
  "ondi_tour_completed",
  "ondi_bubble_pos",
  "ondi_last_greeting_at",
  "queonda_telemetry_queue",
  "queonda_telemetry_last_app_open",
  "onda_ad_walker_seen",
  "queonda_ref_hint",
  "queonda_last_conversation",
  "queonda_pulses_cache",
];

/**
 * Safely set an item in localStorage. On quota overflow, evict safe keys
 * and retry once. Returns true on success, false otherwise. Never throws.
 */
export function safeSetItem(key, value) {
  if (typeof localStorage === "undefined") return false;
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (err) {
    if (!_isQuotaError(err)) {
      // Some non-quota error (private mode, security policy, etc.) — give up.
      try {
        console.debug("[sosStorage] setItem failed (non-quota):", err?.name);
      } catch (_e) {
        /* ignore */
      }
      return false;
    }
    // Evict safe keys and retry.
    _evictSafeKeys();
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (err2) {
      try {
        console.debug(
          "[sosStorage] setItem still failing after eviction:",
          err2?.name
        );
      } catch (_e) {
        /* ignore */
      }
      return false;
    }
  }
}

export function safeGetItem(key, fallback = null) {
  if (typeof localStorage === "undefined") return fallback;
  try {
    const v = localStorage.getItem(key);
    return v == null ? fallback : v;
  } catch (_e) {
    return fallback;
  }
}

export function safeRemoveItem(key) {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(key);
  } catch (_e) {
    /* ignore */
  }
}

function _isQuotaError(err) {
  if (!err) return false;
  // Spec: QuotaExceededError (DOMException code 22) or legacy
  // NS_ERROR_DOM_QUOTA_REACHED (Firefox), or thrown with name match.
  return (
    err.name === "QuotaExceededError" ||
    err.name === "NS_ERROR_DOM_QUOTA_REACHED" ||
    err.code === 22 ||
    err.code === 1014
  );
}

function _evictSafeKeys() {
  for (const k of SAFE_EVICTION_KEYS) {
    if (CRITICAL_KEYS.has(k)) continue; // belt + suspenders
    safeRemoveItem(k);
  }
}

/** Exposed for tests / debug. */
export const _internals = { CRITICAL_KEYS, SAFE_EVICTION_KEYS };
